import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Eye, EyeOff, ArrowLeft, Check } from 'lucide-react';
import api from '../lib/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cd68a189-b98d-45e1-a777-252a17e84c06/artifacts/mloin8cu_Logo%20Metal%20per%20divise%20SVG.svg";

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('reset_token');
    if (t) {
      setResetToken(t);
      setShowResetForm(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const userData = await login(email, password);
      if (userData.must_change_password) {
        navigate('/change-password');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore di autenticazione');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setForgotLoading(true);
    try {
      const res = await api.post('/auth/forgot-password', { email: forgotEmail });
      setForgotSuccess(true);
      // In sviluppo, mostriamo il token per test
      if (res.data.reset_token) {
        setResetToken(res.data.reset_token);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante la richiesta');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError('Le password non coincidono');
      return;
    }

    setResetLoading(true);
    try {
      await api.post('/auth/reset-password', { token: resetToken, new_password: newPassword });
      setResetSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante il reset');
    } finally {
      setResetLoading(false);
    }
  };

  const backToLogin = () => {
    setShowForgotPassword(false);
    setShowResetForm(false);
    setForgotSuccess(false);
    setResetSuccess(false);
    setError('');
    setResetToken('');
    setNewPassword('');
    setConfirmPassword('');
  };

  // Reset password success
  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-sm animate-fade-in">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={24} className="text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">Password Aggiornata</h2>
              <p className="text-sm text-slate-500 mb-4">La tua password e stata aggiornata con successo. Ora puoi accedere con la nuova password.</p>
              <Button onClick={backToLogin} className="w-full bg-[hsl(230,50%,20%)] hover:bg-[hsl(230,50%,25%)]">
                Vai al Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Reset password form (after receiving token)
  if (showResetForm) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-sm animate-fade-in">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-4">
              <button onClick={backToLogin} className="flex items-center text-sm text-slate-500 hover:text-slate-700 mb-2">
                <ArrowLeft size={16} className="mr-1" /> Torna al login
              </button>
              <CardTitle className="text-lg">Nuova Password</CardTitle>
              <CardDescription>Inserisci la tua nuova password</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleResetPassword} className="space-y-4">
                {error && (
                  <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded-lg border border-red-200">
                    {error}
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Token di Reset</Label>
                  <Input
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value)}
                    placeholder="Inserisci il token ricevuto"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nuova Password</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimo 8 caratteri"
                    required
                  />
                  <p className="text-xs text-slate-500">Deve contenere: 8+ caratteri, 1 maiuscola, 1 numero, 1 simbolo</p>
                </div>
                <div className="space-y-2">
                  <Label>Conferma Password</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Ripeti la password"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[hsl(230,50%,20%)] hover:bg-[hsl(230,50%,25%)]"
                  disabled={resetLoading}
                >
                  {resetLoading ? 'Aggiornamento...' : 'Aggiorna Password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Forgot password success
  if (forgotSuccess) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-sm animate-fade-in">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={24} className="text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">Controlla la tua Email</h2>
              <p className="text-sm text-slate-500 mb-4">Se l'email esiste nel sistema, riceverai le istruzioni per il reset della password.</p>
              {resetToken && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-left">
                  <p className="text-xs text-amber-700 font-medium mb-1">Token di sviluppo:</p>
                  <code className="text-xs break-all text-amber-800">{resetToken}</code>
                </div>
              )}
              <div className="space-y-2">
                <Button onClick={() => setShowResetForm(true)} className="w-full bg-[hsl(230,50%,20%)] hover:bg-[hsl(230,50%,25%)]">
                  Ho il Token, Procedi
                </Button>
                <Button variant="outline" onClick={backToLogin} className="w-full">
                  Torna al Login
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Forgot password form
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-sm animate-fade-in">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-4">
              <button onClick={backToLogin} className="flex items-center text-sm text-slate-500 hover:text-slate-700 mb-2">
                <ArrowLeft size={16} className="mr-1" /> Torna al login
              </button>
              <CardTitle className="text-lg">Password Dimenticata</CardTitle>
              <CardDescription>Inserisci la tua email per ricevere le istruzioni di reset</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                {error && (
                  <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded-lg border border-red-200">
                    {error}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="email@azienda.it"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[hsl(230,50%,20%)] hover:bg-[hsl(230,50%,25%)]"
                  disabled={forgotLoading}
                  data-testid="forgot-submit-btn"
                >
                  {forgotLoading ? 'Invio in corso...' : 'Invia Istruzioni'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main login form
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="flex flex-col items-center justify-center gap-4 mb-8">
          <img src={LOGO_URL} alt="Metal Covering S.r.l." className="h-20 w-auto" />
          <div className="text-center">
            <h1 className="text-xl font-bold text-[hsl(230,50%,20%)] tracking-tight">Metal Covering S.r.l.</h1>
            <p className="text-xs text-slate-500 mt-1">Gestionale Operativo Aziendale</p>
          </div>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Accedi</CardTitle>
            <CardDescription>Inserisci le tue credenziali per accedere</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded-lg border border-red-200" data-testid="login-error">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@azienda.it"
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="La tua password"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="toggle-password-btn"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Password dimenticata?
                </button>
              </div>
              <Button
                type="submit"
                className="w-full bg-[hsl(230,50%,20%)] hover:bg-[hsl(230,50%,25%)]"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Accesso in corso...' : 'Accedi'}
              </Button>
            </form>
          </CardContent>
        </Card>
        
        <p className="text-center text-xs text-slate-400 mt-6">
          Gestionale Metal Covering S.r.l. v1.0
        </p>
        
        {/* Creator Branding */}
        <div className="flex items-center justify-center gap-2 mt-4 pb-4">
          <img 
            src="https://customer-assets.emergentagent.com/job_e10f3dc4-f435-40b4-9aef-2385ba08ebcf/artifacts/j4emni6w_Progetto%20senza%20titolo%20%2848%29.png" 
            alt="Wet Industries" 
            className="h-5 w-5 rounded"
          />
          <span className="text-[10px] text-slate-400">
            Created by Gianluca Bagnato with Wet Industries inc. for Metal Covering s.r.l.
          </span>
        </div>
      </div>
    </div>
  );
}
